import { useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import { lookupAccessCodes } from '@/api/accessCodes';
import { getActiveSessionRevision, getActiveSessionType, getAuthTokenForRequest,
  subscribeActiveSessionRevision } from '@/api/authenticatedClient';

export const ACCESS_LOOKUP_WAIT_MS = 15000;
const endpoint = `${API}/access-codes/lookup`;
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl || null, window.lvbDesktop?.localBackendUrl || null]);
const principal = binding => binding?.user?.id && binding?.user?.tenant_id
  && ['cloud', 'offline'].includes(binding.source)
  ? JSON.stringify([binding.user.tenant_id, binding.user.id, binding.source]) : null;
const record = value => value && typeof value === 'object' && !Array.isArray(value);
const optionalStrings = ['instructions', 'community_name', 'community_id', 'provenance', 'verified_at', 'verified_by', 'last_used_at'];
const validPayload = value => record(value) && typeof value.guard_gated === 'boolean' && Array.isArray(value.codes)
  && value.codes.every(code => record(code) && typeof code.id === 'string' && !!code.id
    && typeof code.code === 'string' && typeof code.type === 'string'
    && optionalStrings.every(key => code[key] == null || typeof code[key] === 'string'))
  && new Set(value.codes.map(code => code.id)).size === value.codes.length;

// This owns observations only, not reporting, auth custody, a shared cache or a retry queue.
export function useAccessCodeLookup({ orderId, openingId, addressKey, enabled = true, retainReported = false } = {}) {
  const { sessionBinding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const identity = principal(sessionBinding);
  const bindingRevision = sessionBinding?.revision;
  const source = sessionBinding?.source;
  const connectionKey = connection();
  const [, render] = useState(0);
  const cycle = useMemo(() => ({ orderId, openingId, addressKey, enabled, observedRevision: revision,
    principal: identity, revision: bindingRevision, source,
    connection: connectionKey, active: false, generation: 0, busy: false, controller: null,
    data: null, status: 'loading', reports: new Map(), reportSequence: 0,
  }), [orderId, openingId, addressKey, enabled, identity, bindingRevision, revision, source, connectionKey]);
  const latest = useRef(null);
  latest.current = { cycle, enabled, identity, bindingRevision, source };
  const owns = () => cycle.active && latest.current.cycle === cycle && latest.current.enabled
    && !!cycle.principal && cycle.principal === latest.current.identity
    && cycle.revision === getActiveSessionRevision() && cycle.revision === latest.current.bindingRevision
    && cycle.source === getActiveSessionType() && cycle.source === latest.current.source
    && cycle.connection === connection();
  const publish = () => { if (owns()) render(value => value + 1); };
  const markReported = codeId => {
    if (!owns() || typeof codeId !== 'string' || !codeId) return;
    cycle.reports.set(codeId, ++cycle.reportSequence); publish();
  };
  const retry = async () => {
    if (!owns() || !cycle.orderId || cycle.busy) return false;
    cycle.busy = true; cycle.status = 'loading';
    const generation = ++cycle.generation;
    const reportSequence = cycle.reportSequence;
    const controller = new AbortController(); cycle.controller = controller;
    const current = () => owns() && cycle.generation === generation;
    let timer; let abort;
    publish();
    try {
      // Destination-aware custody refuses an offline token for a cloud URL.
      const token = API !== null && getAuthTokenForRequest(endpoint);
      if (!token) throw new Error('No authorized access-information endpoint');
      const stopped = new Promise((_, reject) => {
        abort = () => reject(new Error('Access lookup stopped'));
        controller.signal.addEventListener('abort', abort, { once: true });
        timer = setTimeout(() => { reject(new Error('Access lookup timed out')); controller.abort(); }, ACCESS_LOOKUP_WAIT_MS);
      });
      const value = await Promise.race([lookupAccessCodes(cycle.orderId, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        timeout: ACCESS_LOOKUP_WAIT_MS, suppressServerErrorNotification: true,
      }), stopped]);
      if (!current()) return false;
      if (!validPayload(value)) throw new Error('Invalid access lookup response');
      // Only reports accepted after this read began defeat its result. A later
      // authoritative read may include an administrator-restored code.
      const returnedIds = new Set(value.codes.map(code => code.id));
      cycle.reports = new Map([...cycle.reports].filter(([id, sequence]) => sequence > reportSequence || !returnedIds.has(id)));
      const retained = retainReported ? (cycle.data?.codes || []).filter(code => cycle.reports.has(code.id) && !returnedIds.has(code.id)) : [];
      cycle.data = { ...value, codes: [...value.codes, ...retained] }; cycle.status = 'ready';
      return true;
    } catch {
      if (current()) cycle.status = 'error';
      return false;
    } finally {
      clearTimeout(timer);
      if (abort) controller.signal.removeEventListener('abort', abort);
      if (current()) { cycle.busy = false; cycle.controller = null; publish(); }
    }
  };
  useLayoutEffect(() => {
    cycle.active = enabled;
    const onReport = event => {
      if (event?.detail?.sessionRevision === cycle.revision) markReported(event.detail.codeId);
    };
    window.addEventListener('access-code-deprecated', onReport);
    retry();
    return () => {
      cycle.active = false; cycle.generation += 1; cycle.busy = false;
      cycle.controller?.abort(); cycle.controller = null;
      window.removeEventListener('access-code-deprecated', onReport);
    };
  // Requests and event handlers belong to the captured opening, not render callbacks.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle, enabled]);

  const visible = enabled && !!identity && bindingRevision === revision
    && source === getActiveSessionType() && cycle.connection === connection();
  const status = !enabled || !orderId ? 'missing' : !visible ? 'retired' : cycle.status;
  const data = visible ? cycle.data : null;
  const reportedCodeIds = visible ? new Set(cycle.reports.keys()) : new Set();
  const loading = status === 'loading';
  const message = status === 'error' ? `Access information unavailable.${data ? ' Showing last confirmed information; not refreshed.' : ''}`
    : status === 'retired' ? 'Access information unavailable. Reopen this screen from your current session.'
      : loading ? (data ? 'Refreshing access information; showing last confirmed information.' : 'Checking access information…')
        : status === 'missing' && enabled ? 'Access information unavailable for an unsaved order.' : '';
  return { codes: data ? data.codes.filter(code => retainReported || !reportedCodeIds.has(code.id)) : [],
    guardGated: data?.guard_gated || false, status, message, loading,
    refreshing: loading && !!data, hasData: !!data, retry,
    canRetry: visible && !!orderId && !cycle.busy, reportedCodeIds, markReported, owns };
}
